import { Component } from "@theme/component";
import { fetchConfig } from "@theme/utilities";
import { ThemeEvents } from "@theme/events";

// Constants
const POSTAL_CODE_REGEX = /^[a-zA-Z0-9\s-]{3,10}$/;
const HTTP_STATUS = {
  UNPROCESSABLE_ENTITY: 422
};

/**
 * A custom element that manages country and province selection for shipping forms.
 *
 * @typedef {object} CountryProvinceRefs
 * @property {HTMLSelectElement} countryInput - The country select input.
 * @property {HTMLSelectElement} provinceInput - The province select input.
 *
 * @extends Component<CountryProvinceRefs>
 */
export class CountryProvinceComponent extends Component {
  requiredRefs = ["countryInput", "provinceInput"];

  /** @type {string | null} */
  #defaultCountry = null;

  /** @type {string | null} */
  #defaultProvince = null;

  /** @type {boolean} */
  #hasPopulatedOptions = false;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  /** @type {string | null} */
  #currentSelectedCountry = null;

  /** @type {string | null} */
  #currentSelectedProvince = null;

  get container() {
    return this.closest('.dialog') || this.closest('details');
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize with default country if provided
    this.#initDefaultCountry();

    // Handle country change
    this.boundOnCountryChange = this.#onCountryChange.bind(this);
    this.refs.countryInput.addEventListener("change", this.boundOnCountryChange);

    // Use MutationObserver to detect when container (dialog or details) opens
    this.#setupContainerObserver();

    this.boundOnCartUpdate = this.#onCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.boundOnCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.boundOnCartUpdate) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.boundOnCartUpdate);
    }
    if (this.boundOnCountryChange) {
      this.refs.countryInput.removeEventListener("change", this.boundOnCountryChange);
    }

    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
  }

  /**
   * Handle container open (dialog or details) to populate country options
   */
  #onContainerOpen() {
    if (this.#hasPopulatedOptions) return;

    if (this.container && (this.container.open || this.container.hasAttribute('open'))) {
      this.#ensureCountryOptions();
      this.#hasPopulatedOptions = true;

      if (this.#defaultCountry) {
        this.#applyDefaultCountry(this.#defaultCountry, this.#defaultProvince);
        this.#defaultCountry = null;
        this.#defaultProvince = null;
      }
    }
  }

  /**
   * Initialize the country selection based on data attributes
   */
  #initDefaultCountry() {
    this.#defaultCountry = this.dataset.country;
    this.#defaultProvince = this.dataset.province;
  }

  /**
   * Populate country options from <template> (lazy loading)
   */
  #renderCountryOptionsFromTemplate() {
    const template = this.refs.countryInput.querySelector("template");
    if (template) {
      const templateContent = document.importNode(template.content, true);
      this.refs.countryInput.appendChild(templateContent);
    }
  }

  /**
   * Ensure country options are populated before handling change
   */
  #ensureCountryOptions() {
    const template = this.refs.countryInput.querySelector("template");
    if (template) {
      this.#renderCountryOptionsFromTemplate();
    }
  }

  /**
   * Set default country and province after options are populated
   * @param {string} countryData - Country name
   * @param {string} provinceData - Province name
   */
  #applyDefaultCountry(countryData, provinceData) {
    const countryOption = Array.from(this.refs.countryInput.options).find(
      (option) => option.textContent === countryData
    );

    if (countryOption) {
      this.refs.countryInput.selectedIndex = countryOption.index;
      this.refs.countryInput.dispatchEvent(new Event("change"));
    }
  }

  /**
   * Setup MutationObserver to detect when container (dialog or details) opens
   */
  #setupContainerObserver() {
    if (!this.container) return;

    this.#dialogObserver = new MutationObserver((mutations) => {
      // Use some() for early exit when we find the mutation we're looking for
      mutations.some((mutation) => {
        const containerOpen = this.container?.open || this.container?.hasAttribute('open');
        if (mutation.type === "attributes" && 
            mutation.attributeName === "open" && 
            containerOpen) {
          this.#onContainerOpen();
          return true; // Stop processing other mutations
        }
        return false;
      });
    });

    this.#dialogObserver.observe(this.container, {
      attributes: true,
      attributeFilter: ["open"],
    });
  }

  /**
   * Handles country selection change
   * Updates province options based on selected country
   * Stores current selection for restoration after DOM morph
   */
  #onCountryChange() {
    const selectedOption = this.refs.countryInput.options[this.refs.countryInput.selectedIndex];
    if (!selectedOption) return;

    // Store current selection for restoration after DOM morph
    this.#currentSelectedCountry = selectedOption.value;
    this.#currentSelectedProvince = null; // Reset province when country changes

    const provincesData = selectedOption.dataset.provinces;
    if (!provincesData) {
      this.#toggleProvinceField(false);
      return;
    }

    try {
      const provinces = JSON.parse(provincesData);
      if (!Array.isArray(provinces) || provinces.length === 0) {
        this.#toggleProvinceField(false);
        return;
      }

      this.#toggleProvinceField(true);
      this.#renderProvinceOptions(provinces);
    } catch (error) {
      console.error("Error parsing provinces data:", error);
      this.#toggleProvinceField(false);
      // Could emit a custom event here for better error handling
      this.dispatchEvent(new CustomEvent('province-parse-error', {
        detail: { error, provincesData },
        bubbles: true
      }));
    }
  }

  /**
   * Toggle visibility of the province field
   * @param {boolean} isVisible
   */
  #toggleProvinceField(isVisible) {
    const provinceField = this.refs.provinceInput.closest(".cart-shipping__field");
    if (provinceField) {
      provinceField.hidden = !isVisible;
    }
  }

  /**
   * Update province options based on selected country
   * @param {Array<Array<string>>} provinces - Array of province data [code, name]
   */
  #renderProvinceOptions(provinces) {
    // Clear existing options more efficiently
    this.refs.provinceInput.replaceChildren();

    // Use DocumentFragment for better performance when adding multiple options
    const fragment = document.createDocumentFragment();
    
    provinces.forEach(([code, name]) => {
      const isSelected = code === this.dataset.province || code === this.#currentSelectedProvince;
      const option = new Option(name, code, isSelected, isSelected);
      fragment.appendChild(option);
      
      // Store selected province for restoration
      if (isSelected) {
        this.#currentSelectedProvince = code;
      }
    });
    
    this.refs.provinceInput.appendChild(fragment);
  }

  /**
   * Handle cart update events to reinitialize after DOM morph
   * @param {Event} event - Cart update event
   */
  #onCartUpdate(event) {
    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      this.#reinitializeAfterMorph();
    });
  }

  /**
   * Reinitialize component after DOM morph
   */
  #reinitializeAfterMorph() {
    // Check if options are still populated
    if (this.refs.countryInput.options.length <= 1) {
      // Options were cleared, need to repopulate
      this.#hasPopulatedOptions = false;
      this.#ensureCountryOptions();
      this.#hasPopulatedOptions = true;
    }

    // Restore previous selections if they exist
    if (this.#currentSelectedCountry) {
      this.#restoreSelections();
    } else if (this.#defaultCountry) {
      // Fallback to default values
      this.#applyDefaultCountry(this.#defaultCountry, this.#defaultProvince);
    }
  }

  /**
   * Restore previous country and province selections
   */
  #restoreSelections() {
    // Restore country selection
    const countryOption = Array.from(this.refs.countryInput.options).find(
      (option) => option.value === this.#currentSelectedCountry
    );

    if (countryOption) {
      this.refs.countryInput.selectedIndex = countryOption.index;
      
      // Trigger country change to populate provinces
      this.refs.countryInput.dispatchEvent(new Event("change"));
      
      // Restore province selection after a short delay to allow provinces to load
      if (this.#currentSelectedProvince) {
        setTimeout(() => {
          const provinceOption = Array.from(this.refs.provinceInput.options).find(
            (option) => option.value === this.#currentSelectedProvince
          );
          
          if (provinceOption) {
            this.refs.provinceInput.selectedIndex = provinceOption.index;
          }
        }, 50);
      }
    }
  }

  /**
   * Called by morph system after DOM update
   * This is automatically called by the morph system
   */
  updatedCallback() {
    this.#reinitializeAfterMorph();
  }
}

if (!customElements.get("country-province-component")) {
  customElements.define("country-province-component", CountryProvinceComponent);
}

/**
 * A custom element that manages shipping calculation form.
 *
 * @typedef {object} CartShippingRefs
 * @property {HTMLFormElement} form - The shipping calculation form.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} results - The results container.
 * @property {HTMLInputElement} zipInput - The zip code input.
 * @property {CountryProvinceComponent} countryProvince - The country-province component.
 *
 * @extends Component<CartShippingRefs>
 */
export class CartShippingComponent extends Component {
  requiredRefs = ["form", "submitButton", "results", "zipInput", "countryProvince"];

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {AbortController | null} */
  #currentFetchController = null;

  /** @type {string} */
  #defaultErrorMessage = Theme.translations.shipping_calculator_error;

  connectedCallback() {
    super.connectedCallback();
    this.refs.form.addEventListener("submit", this.#onFormSubmit);

    if (Shopify?.designMode) {
      this.refs.submitButton.type = "button";
      this.boundHandleDesignModeSubmit = this.#handleDesignModeSubmit.bind(this);
      this.refs.submitButton.addEventListener("click", this.boundHandleDesignModeSubmit);
    }
    
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form.removeEventListener("submit", this.#onFormSubmit);
    if (this.boundOnCountryChange && this.refs.countryInput) {
      this.refs.countryInput.removeEventListener("change", this.boundOnCountryChange);
    }
    if (this.boundHandleDesignModeSubmit) {
      this.refs.submitButton.removeEventListener("click", this.boundHandleDesignModeSubmit);
    }
    if (this.boundOnCartUpdate) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.boundOnCartUpdate);
    }
  }

  /**
   * Handle form submission for shipping calculation
   * @param {Event} event - The submit event
   */
  #onFormSubmit = (event) => {
    event?.preventDefault();

    if (this.#isSubmitting) return;

    const formData = this.#extractFormData();
    const validationError = this.#validateForm(formData.zip, formData.country);
    
    if (validationError) {
      this.#renderErrors([validationError]);
      this.refs.results.hidden = false;
      return;
    }

    this.#submitShippingRequest(formData);
  }

  /**
   * Handles form submission in Shopify design mode
   * @param {Event} event - Click event
   */
  #handleDesignModeSubmit = (event) => {
    event.preventDefault();
    this.#onFormSubmit(event);
  };

  /**
   * Extract form data from inputs
   * @returns {Object} Form data object
   */
  #extractFormData() {
    return {
      zip: this.refs.zipInput.value.trim(),
      country: this.refs.countryProvince.refs.countryInput.value,
      province: this.refs.countryProvince.refs.provinceInput.value
    };
  }

  /**
   * Submit shipping calculation request
   * @param {Object} formData - Form data object
   */
  async #submitShippingRequest(formData) {
    this.#isSubmitting = true;
    this.#setLoadingState();

    const body = JSON.stringify({ shipping_address: formData });
    const sectionUrl = `${Theme.routes.cart_url}/shipping_rates.json`;
    
    // Abort previous fetch if still pending
    this.#currentFetchController?.abort();
    
    // Create new controller for this specific fetch
    this.#currentFetchController = new AbortController();

    try {
      const response = await fetch(sectionUrl, {
        ...fetchConfig("json", { body }),
        signal: this.#currentFetchController.signal,
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
          this.#handleServerErrors(data);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } else if (data.shipping_rates) {
        this.#renderShippingRates(data.shipping_rates);
      } else {
        this.#handleServerErrors(data);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Shipping calculation error:", error);
      this.#renderErrors([this.#defaultErrorMessage]);
    } finally {
      this.#isSubmitting = false;
      this.#clearLoadingState();
      this.refs.results.hidden = false;
      // Clear the reference after fetch completes
      this.#currentFetchController = null;
    }
  }

  /**
   * Validate form data
   * @param {string} zip - Postal code
   * @param {string} country - Country code
   * @returns {string|null} Error message or null if valid
   */
  #validateForm(zip, country) {
    if (!zip) return Theme.translations.please_enter_a_postal_code;
    if (!country) return Theme.translations.please_select_a_country;
    if (!POSTAL_CODE_REGEX.test(zip)) {
      return Theme.translations.please_enter_a_valid_postal_code;
    }
    return null;
  }

  /**
   * Add loading state to submit button
   */
  #setLoadingState() {
    this.refs.submitButton.classList.add("btn--loading");
    this.refs.submitButton.disabled = true;
  }

  /**
   * Remove loading state from submit button
   */
  #clearLoadingState() {
    this.refs.submitButton.classList.remove("btn--loading");
    this.refs.submitButton.disabled = false;
  }

  /**
   * Render generic error messages
   */
  #renderErrors(errorMessages) {
    this.refs.results.innerHTML = `
      <div class="form__error-list" role="alert" aria-live="polite">
        <div class="form__error">
          <span class="svg-icon">
            ${ Theme.icons.warning }
          </span>
          ${Theme.translations.shipping_calculator_error_message}
        </div>
        ${
          `<ul class="list-disc" role="list">${errorMessages.map((msg) => `<li>${this.#escapeHtml(msg)}</li>`).join("")}</ul>`
        }
      </div>
    `;
  }

  /**
   * Render success messages
   */
  #renderSuccess(messageLabel, messagesList) {
    this.refs.results.innerHTML = `
      <div class="form__success-list" role="alert" aria-live="polite">
        <div class="form__success">
          <span class="svg-icon">
            ${ Theme.icons.info }
          </span>
          ${messageLabel}
        </div>
        ${
          `<ul class="list-disc" role="list">${messagesList}</ul>`
        }
      </div>
    `;
  }

  /**
   * Handles server error responses
   * Extracts error messages and renders them
   * @param {Object} response - Error response object
   */
  #handleServerErrors(response) {
    const errorMessages = this.#extractErrorMessages(response);
    this.#renderErrors(errorMessages);
  }

  /**
   * Extracts error messages from various response formats
   * Handles string, object with message, field errors, and errors array
   * @param {Object|string} response - Error response
   * @returns {string[]} Array of error messages
   */
  #extractErrorMessages(response) {
    const errorMessages = [];
    
    if (typeof response === "string") {
      errorMessages.push(response);
      return errorMessages;
    }

    if (response.message) {
      errorMessages.push(response.message);
      return errorMessages;
    }

    // Check for field-level errors
    const hasFieldErrors = Object.keys(response).some((key) => Array.isArray(response[key]));
    if (hasFieldErrors) {
      Object.entries(response).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          errorMessages.push(...messages);
        } else {
          errorMessages.push(messages);
        }
      });
      return errorMessages;
    }

    // Check for errors object
    if (response.errors) {
      Object.entries(response.errors).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          errorMessages.push(...messages);
        } else {
          errorMessages.push(messages);
        }
      });
      return errorMessages;
    }

    // Fallback to default error message
    errorMessages.push(this.#defaultErrorMessage);
    return errorMessages;
  }

  /**
   * Renders shipping rates in results container
   * Formats rates with proper currency and handles empty rates
   * @param {Array} shippingRates - Array of shipping rate objects
   */
  #renderShippingRates(shippingRates) {
    if (!Array.isArray(shippingRates) || shippingRates.length === 0) {
      this.#renderErrors([Theme.translations.shipping_calculator_not_found]);
      return;
    }

    const shippingRatesList = shippingRates
      .map(({ presentment_name, currency, price }) => {
        // Escape HTML to prevent XSS
        const name = this.#escapeHtml(presentment_name);
        const currencySymbol = this.#escapeHtml(currency);
        const priceValue = this.#escapeHtml(price);
        return `<li>${name}: ${currencySymbol} ${priceValue}</li>`;
      })
      .join("");

    const message = this.#shippingMessageForCount(shippingRates.length);

    this.#renderSuccess(message, shippingRatesList);
  }

  /**
   * Escapes HTML special characters to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Returns localized shipping message based on rate count
   * @param {number} count - Number of shipping rates
   * @returns {string} Localized message
   */
  #shippingMessageForCount(count) {
    if (count === 1) {
      return Theme.translations.shipping_calculator_one_result;
    }
    const message = Theme.translations.shipping_calculator_multiple_results;
    return message.includes("{{ count }}") ? message.replace("{{ count }}", count.toString()) : message;
  }


  /**
   * Called by morph system after DOM update
   * This is automatically called by the morph system
   */
  updatedCallback() {
    // Ensure country-province component is properly reinitialized
    if (this.refs.countryProvince && typeof this.refs.countryProvince.updatedCallback === 'function') {
      this.refs.countryProvince.updatedCallback();
    }
  }
}

if (!customElements.get("cart-shipping-component")) {
  customElements.define("cart-shipping-component", CartShippingComponent);
}
